import axios, { AxiosError } from 'axios';
import { Upload, FileText, Download, Info } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { FileList } from './components/FileList';
import { FileUpload } from './components/FileUpload';
import { TemplateFieldsModal } from './components/TemplateFieldsModal';

import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useAuth } from '@/contexts/AuthContext';
import { useCreateCsv } from '@/hooks/useCreateCsv';
import { useDownloadCsv } from '@/hooks/useDownloadCsv';
import { useGetSignedUrl } from '@/hooks/useGetSignedUrl';
import { CSVFile, CSVType } from '@/services/CSVService';

export function ProductsManager() {
  const [sheetType, setSheetType] = useState<CSVType>(CSVType.PRODUCT);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { t } = useTranslation('translation', {
    keyPrefix: 'pages.Settings.components.ProductsManager',
  });

  const { createCsv, isLoading: isUploading } = useCreateCsv();
  const { generateDownloadUrl } = useDownloadCsv();
  const [isUploadingToS3, setIsUploadingToS3] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [downloadUrls, setDownloadUrls] = useState<Record<string, string>>({});
  const [generatingFileId, setGeneratingFileId] = useState<string | null>(null);

  const { refetch } = useGetSignedUrl();

  const { user } = useAuth();

  async function handleFileUpload(file: File) {
    if (!file) return;

    try {
      setIsUploadingToS3(true);

      const { data: newSignedUrl } = await refetch();

      if (!newSignedUrl) {
        throw new Error('Failed to get signed URL');
      }

      const response = await axios.put(newSignedUrl.url, file, {
        headers: {
          'Content-Type': 'text/csv',
        },
      });

      if (response.status === 200) {
        await createCsv({
          fileKey: newSignedUrl.fileKey,
          originalName: file.name,
          type: sheetType,
        });
        toast.success(
          t(`${sheetType}.components.FileUpload.toast.uploadSuccess`, {
            filename: file.name,
          }),
        );
        setSelectedFile(null);
      }
    } catch (error) {
      toast.error(
        t(`${sheetType}.components.FileUpload.toast.uploadError`, {
          filename: file.name,
          error,
        }),
      );
    } finally {
      setIsUploadingToS3(false);
    }
  }

  async function handleDownloadFile(file: CSVFile) {
    const existingUrl = downloadUrls[file.id];

    if (existingUrl) {
      window.open(existingUrl);
      setDownloadUrls((prev) => {
        const newUrls = { ...prev };
        delete newUrls[file.id];
        return newUrls;
      });
    } else {
      try {
        setGeneratingFileId(file.id);
        const { url } = await generateDownloadUrl({ fileKey: file.fileKey });
        setDownloadUrls((prev) => ({ ...prev, [file.id]: url }));
      } catch (error) {
        toast.error(
          error instanceof AxiosError
            ? error.response?.data?.message
            : 'Failed to generate download link',
        );
      } finally {
        setGeneratingFileId(null);
      }
    }
  }

  function handleDownloadTemplate() {
    let url: string | null = '';

    if (sheetType === CSVType.SHOP_THE_LOOK) {
      url =
        'https://fashionai.nyc3.cdn.digitaloceanspaces.com/models/template_shop_the_look.csv';
    }

    if (sheetType === CSVType.PRODUCT) {
      if (user?.customer.group === 'AREZZO') {
        url =
          'https://fashionai.nyc3.cdn.digitaloceanspaces.com/models/arezzo_exemplo_v3.csv';
      } else {
        url =
          'https://fashionai.nyc3.cdn.digitaloceanspaces.com/models/products_upload_v2.csv';
      }
    }

    window.open(url, '_blank');
    setTimeout(() => {
      window.URL.revokeObjectURL(url);
    }, 100);
  }

  return (
    <>
      <TemplateFieldsModal
        open={isModalOpen}
        onOpenChange={setIsModalOpen}
        sheetType={sheetType}
      />

      <Card>
        <CardHeader>
          <CardTitle>{t('title')}</CardTitle>
          <CardDescription className="text-muted-foreground">
            {t(`${sheetType}.description`)}
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <Upload className="h-5 w-5" />
                  {t(`${sheetType}.components.FileUpload.title`)}
                </div>
                <div className="mr-4 flex justify-center gap-2">
                  <Select
                    value={sheetType}
                    onValueChange={(value: CSVType) => setSheetType(value)}
                  >
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder={t('sheetTypePlaceholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={CSVType.PRODUCT}>
                        {t('products')}
                      </SelectItem>
                      <SelectItem value={CSVType.SHOP_THE_LOOK}>
                        {t('shopTheLook')}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="outline"
                    onClick={handleDownloadTemplate}
                    className="flex items-center gap-2 whitespace-nowrap bg-transparent"
                  >
                    <Download className="h-4 w-4" />
                    {t('downloadTemplateButton')} -{' '}
                    {sheetType === CSVType.PRODUCT
                      ? t('products')
                      : t('shopTheLook')}
                  </Button>
                  <div className="rounded-md border border-blue-200 bg-blue-50 px-3 py-2 dark:border-blue-800 dark:bg-blue-950/30">
                    <button
                      onClick={() => setIsModalOpen(true)}
                      className="flex items-center gap-2 whitespace-nowrap text-sm font-medium text-blue-700 transition-all hover:underline dark:text-blue-400"
                    >
                      <Info className="h-4 w-4" />
                      {t('templateFieldsButton')}
                    </button>
                  </div>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <FileUpload
                onSetSelectedFile={setSelectedFile}
                selectedFile={selectedFile}
                onFileUpload={handleFileUpload}
                isLoading={isUploadingToS3 || isUploading}
                sheetType={sheetType}
              />
            </CardContent>
          </Card>

          <Card className="w-full">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5" />
                {t('components.FileList.title')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <FileList
                onDownloadFile={handleDownloadFile}
                generatingFileId={generatingFileId}
                downloadUrls={downloadUrls}
                queryFilterDto={{ type: sheetType }}
              />
            </CardContent>
          </Card>
        </CardContent>
      </Card>
    </>
  );
}
